/**
 * What the BYO source and destination configure steps share: the Continue
 * save wiring, the hydration loader + save-error frame, and the retry-safe
 * create-or-update of the entity a step persists.
 */

import React, { useEffect, useState } from 'react';
import { Alert } from '@mui/material';
import { normalizeCredentialId } from 'js/lib/utils/credentialUtils';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { useForm } from 'new/elements/Form/useForm';
import classes from './byoConfigureStep.module.scss';

type EntityId = number | string;

/**
 * Registers `save` as the form's Continue handler and returns its error.
 * `save` must resolve truthy on success: useForm's nextPage() only disarms
 * the override on a truthy result, so an implicit `undefined` re-arms
 * Continue and a second click re-runs the whole save (duplicate entities —
 * fixed 2026-08-19). A throw is shown inline instead of propagating into
 * nextPage(), which only resets loading and shows nothing (fixed 2026-08-20).
 */
export function useByoConfigureStepSave(
  save: () => Promise<boolean | undefined>,
  failureMessage: string,
): string | null {
  const { overrideNextFunction } = useForm();
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    overrideNextFunction(async () => {
      setSaveError(null);
      try {
        return await save();
      } catch (e: any) {
        setSaveError(e?.message ?? failureMessage);
        return undefined;
      }
    });
    return () => overrideNextFunction(null);
  }, [save, failureMessage, overrideNextFunction]);

  return saveError;
}

/**
 * Edit mode can mount a step before useStartSource/useStartSink's async
 * hydration lands. Mounting FileSystem early breaks it twice: DataSourceDetails'
 * Name field is an uncontrolled input that locks in the empty value, and
 * SelectBucket only probes folders when selectedBucket/credentialId flip in a
 * later update — not when both start out "set" (to undefined). The legacy
 * DirectFlow SourceConfigureStep guards the same way.
 */
export function ByoConfigureStepFrame({
  hydrating,
  saveError,
  children,
}: {
  hydrating: boolean;
  saveError: string | null;
  children: React.ReactNode;
}) {
  if (hydrating) return <CenteredLoader />;
  return (
    <div className={classes.wrap}>
      {saveError ? (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
          {saveError}
        </Alert>
      ) : null}
      {children}
    </div>
  );
}

/** `data_credentials_id` for a create/update body — omitted, never null, when there is none. */
export function credentialPatch(credentialId: unknown): { data_credentials_id?: EntityId } {
  const id = normalizeCredentialId(credentialId);
  return id != null ? { data_credentials_id: id } : {};
}

/**
 * Retry-safe persist for a step's entity. The first Continue creates it and
 * records its id in `progress`; a Continue retried after a LATER call failed
 * reuses it, updating it only when the inputs changed (the user went Back and
 * picked another connector, folder or credential) — never a duplicate POST,
 * never a stale config. `progress` lives in StudioDraftProvider so it
 * survives Back unmounting the step.
 */
export async function createOnceOrUpdate<K extends string>(
  progress: Record<K, EntityId | null> & { inputsKey: string | null },
  idKey: K,
  inputs: unknown[],
  {
    create,
    update,
    createFailedMessage,
  }: {
    create: () => Promise<EntityId | null | undefined>;
    update: (id: EntityId) => Promise<unknown>;
    createFailedMessage: string;
  },
): Promise<EntityId> {
  const inputsKey = JSON.stringify(inputs);
  const existingId = progress[idKey];
  if (existingId == null) {
    const createdId = await create();
    if (createdId == null || createdId === '') throw new Error(createFailedMessage);
    progress[idKey] = createdId as (typeof progress)[K];
  } else if (progress.inputsKey !== inputsKey) {
    await update(existingId);
  }
  progress.inputsKey = inputsKey;
  return progress[idKey] as EntityId;
}
